import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { usePageSEO } from '@/hooks/usePageSEO'
import { 
  Shield, Users, Calendar, BarChart3, Settings, Database,
  BookOpen, FileText, Award, MessageSquare,
  GraduationCap, Eye, Bell, Heart, ChevronRight
} from 'lucide-react'

export const Route = createFileRoute('/features')({
  component: FeaturesPage,
})

function FeaturesPage() {
  const { t } = useTranslation('features')
  const mainColor = '#525FE1'
  usePageSEO({
    title: 'Fonctionnalites JeridSchool - Gestion scolaire en Tunisie',
    description:
      'Explorez les modules JeridSchool pour administrateurs, enseignants, eleves et parents : emplois du temps, presences, communication et suivi scolaire.',
    keywords:
      'fonctionnalites logiciel scolaire, gestion ecole Tunisie, module presence, emploi du temps automatique, portail parents',
    path: '/features',
  })

  const allFeatures = [
    {
      role: 'admin',
      roleLabel: t('cards.admin.title'),
      roleHref: '/feature/admin',
      features: [
        { icon: Users, title: t('admin.userManagement.title'), description: t('admin.userManagement.description'), href: '/feature/admin' },
        { icon: Calendar, title: t('admin.timetable.title'), description: t('admin.timetable.description'), href: '/feature/admin' },
        { icon: BarChart3, title: t('admin.analytics.title'), description: t('admin.analytics.description'), href: '/feature/admin' },
        { icon: Shield, title: t('admin.security.title'), description: t('admin.security.description'), href: '/feature/admin' },
        { icon: Database, title: t('admin.dataManagement.title'), description: t('admin.dataManagement.description'), href: '/feature/admin' },
        { icon: Settings, title: t('admin.systemSettings.title'), description: t('admin.systemSettings.description'), href: '/feature/admin' }
      ]
    },
    {
      role: 'teacher',
      roleLabel: t('cards.teacher.title'),
      roleHref: '/feature/teacher',
      features: [
        { icon: BookOpen, title: t('teacher.lessonPlanning.title'), description: t('teacher.lessonPlanning.description'), href: '/feature/teacher' },
        { icon: Users, title: t('teacher.studentManagement.title'), description: t('teacher.studentManagement.description'), href: '/feature/teacher' },
        { icon: Calendar, title: t('teacher.attendance.title'), description: t('teacher.attendance.description'), href: '/feature/teacher' },
        { icon: FileText, title: t('teacher.assessments.title'), description: t('teacher.assessments.description'), href: '/feature/teacher' },
        { icon: Award, title: t('teacher.grading.title'), description: t('teacher.grading.description'), href: '/feature/teacher' },
        { icon: MessageSquare, title: t('teacher.communication.title'), description: t('teacher.communication.description'), href: '/feature/teacher' }
      ]
    },
    {
      role: 'student',
      roleLabel: t('cards.student.title'),
      roleHref: '/feature/student',
      features: [
        { icon: BookOpen, title: t('student.learning.title'), description: t('student.learning.description'), href: '/feature/student' },
        { icon: Calendar, title: t('student.schedule.title'), description: t('student.schedule.description'), href: '/feature/student' },
        { icon: Award, title: t('student.achievements.title'), description: t('student.achievements.description'), href: '/feature/student' },
        { icon: MessageSquare, title: t('student.communication.title'), description: t('student.communication.description'), href: '/feature/student' },
        { icon: Users, title: t('student.collaboration.title'), description: t('student.collaboration.description'), href: '/feature/student' },
        { icon: GraduationCap, title: t('student.progress.title'), description: t('student.progress.description'), href: '/feature/student' }
      ]
    },
    {
      role: 'parent',
      roleLabel: t('cards.parent.title'),
      roleHref: '/feature/parent',
      features: [
        { icon: Eye, title: t('parent.monitoring.title'), description: t('parent.monitoring.description'), href: '/feature/parent' },
        { icon: MessageSquare, title: t('parent.communication.title'), description: t('parent.communication.description'), href: '/feature/parent' },
        { icon: Calendar, title: t('parent.schedule.title'), description: t('parent.schedule.description'), href: '/feature/parent' },
        { icon: Award, title: t('parent.progress.title'), description: t('parent.progress.description'), href: '/feature/parent' },
        { icon: Bell, title: t('parent.notifications.title'), description: t('parent.notifications.description'), href: '/feature/parent' },
        { icon: Heart, title: t('parent.support.title'), description: t('parent.support.description'), href: '/feature/parent' }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('hero.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('hero.description')}
          </p>
        </div>

        {/* All Features by Role */}
        <div className="space-y-12">
          {allFeatures.map((roleGroup, ) => (
            <div key={roleGroup.role} className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              {/* Role Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: mainColor }}
                  >
                    {roleGroup.role === 'admin' && <Shield className="w-6 h-6 text-white" />}
                    {roleGroup.role === 'teacher' && <BookOpen className="w-6 h-6 text-white" />}
                    {roleGroup.role === 'student' && <GraduationCap className="w-6 h-6 text-white" />}
                    {roleGroup.role === 'parent' && <Heart className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{roleGroup.roleLabel}</h2>
                    <p className="text-sm text-gray-500">{roleGroup.features.length} features</p>
                  </div>
                </div>
                <Link
                  to={roleGroup.roleHref}
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-90"
                  style={{ backgroundColor: mainColor, color: 'white' }}
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roleGroup.features.map((feature, featureIndex) => (
                  <Link
                    key={featureIndex}
                    to={feature.href}
                    className="group p-5 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${mainColor}15` }}
                      >
                        <feature.icon className="w-5 h-5" style={{ color: mainColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {feature.description}
                        </p>
                      </div>
                      <ChevronRight 
                        className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0 mt-1"
                      />
                    </div>
                  </Link>
                ))}
              </div>

              {/* Mobile View All Button */}
              <div className="mt-6 md:hidden">
                <Link
                  to={roleGroup.roleHref}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg font-medium transition-colors hover:opacity-90"
                  style={{ backgroundColor: mainColor, color: 'white' }}
                >
                  View All {roleGroup.roleLabel} Features
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('cta.title')}
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register"
              className="text-white px-8 py-3 rounded-lg font-semibold transition-colors hover:opacity-90"
              style={{ backgroundColor: mainColor }}
            >
              {t('cta.getStarted')}
            </a>
            <a
              href="/demo"
              className="border px-8 py-3 rounded-lg font-semibold transition-colors hover:bg-blue-50"
              style={{ borderColor: mainColor, color: mainColor }}
            >
              {t('cta.scheduleDemo')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
